import React, { useEffect, useState, useCallback } from "react";
import { Card, Badge, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

export default function IslamicTeachingTracking() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const [yearId, setYearId]         = useState(() => localStorage.getItem("yearId") || null);
  const [yearList, setYearList]     = useState([]);
  const [classes, setClasses]       = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [rooms, setRooms]           = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [teachings, setTeachings]   = useState([]);

  // Load study years
  useEffect(() => {
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`)
      .then(res => {
        const list = res.data;
        setYearList(list);
        let id = localStorage.getItem("yearId");
        if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
        setYearId(id);
      })
      .catch(() => {});
  }, []);

  // Load Islamic classes
  useEffect(() => {
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic/getIslamicClasses`)
      .then(res => {
        setClasses(res.data);
        if (res.data.length > 0) setSelectedClass(res.data[0]);
      })
      .catch(() => {});
  }, []);

  // Load rooms when year or class changes
  useEffect(() => {
    if (!yearId || !selectedClass) return;
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic/getRooms/${yearId}`)
      .then(res => {
        const filtered = res.data.filter(r => String(r.ic_id) === String(selectedClass.ic_id));
        setRooms(filtered);
        setSelectedRoom(filtered.length > 0 ? filtered[0] : null);
      })
      .catch(() => { setRooms([]); setSelectedRoom(null); })
      .finally(() => setLoading(false));
  }, [yearId, selectedClass, setLoading]);

  // Load teachings when room changes
  const fetchTeachings = useCallback((ir_id) => {
    if (!ir_id) return;
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/teachingsInRoom/${ir_id}`)
      .then(res => setTeachings(res.data))
      .catch(() => setTeachings([]))
      .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => {
    if (selectedRoom) fetchTeachings(selectedRoom.ir_id);
    else setTeachings([]);
  }, [selectedRoom, fetchTeachings]);

  const renderSlotSummary = (slot_summary) => {
    if (!slot_summary) return <span className="text-muted small">—</span>;
    const groups = {};
    slot_summary.split(',').forEach(pair => {
      const [d, p] = pair.split(':');
      if (!groups[d]) groups[d] = [];
      groups[d].push(p);
    });
    return (
      <div className="d-flex flex-wrap gap-1">
        {Object.entries(groups).map(([day, periods]) => (
          <span key={day} className="badge bg-light text-dark border small">
            {t.days[Number(day)]} : {periods.join(', ')}
          </span>
        ))}
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="main-title mb-0">{t.teachingTracking.pageTitle}</h4>
        <Form.Select
          className="w-auto"
          value={yearId || ""}
          onChange={e => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }}
        >
          {yearList.map(y => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </Form.Select>
      </div>

      {/* Class tabs */}
      <div className="d-flex gap-1 mb-3 flex-wrap">
        {classes.map(cls => (
          <button
            key={cls.ic_id}
            className={`btn btn-sm ${selectedClass?.ic_id === cls.ic_id ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => { setSelectedClass(cls); setSelectedRoom(null); setTeachings([]); }}
          >
            {cls.ic_class}
          </button>
        ))}
      </div>

      <Card className="card-one">
        <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
          <Card.Title as="h6" className="mb-0">{t.teachingTracking.classRoom}</Card.Title>
          {rooms.length > 0 && (
            <Form.Select
              size="sm"
              className="w-auto ms-auto"
              value={selectedRoom?.ir_id || ""}
              onChange={e => {
                const rm = rooms.find(r => String(r.ir_id) === e.target.value);
                setSelectedRoom(rm || null);
              }}
            >
              {rooms.map(r => (
                <option key={r.ir_id} value={r.ir_id}>
                  {r.ic_class}/{r.ir_no}
                  {r.is_programCode ? ` (${r.is_programCode})` : ""}
                  {" — "}{r.ir_gender === "m" ? t.common.male : t.common.female}
                </option>
              ))}
            </Form.Select>
          )}
        </Card.Header>

        <Card.Body className="p-0">
          {!selectedRoom || rooms.length === 0 ? (
            <div className="text-center text-secondary py-5">
              <i className="ri-information-line fs-3 d-block mb-2"></i>
              {t.teachingTracking.noRooms}
            </div>
          ) : teachings.length === 0 ? (
            <div className="text-center text-secondary py-5">
              <i className="ri-book-open-line fs-3 d-block mb-2"></i>
              {t.teachingTracking.noTeachings}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0 small">
                <thead className="table-secondary">
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th style={{ width: 90 }}>{t.teachingTracking.colCode}</th>
                    <th>{t.teachingTracking.colSubjectName}</th>
                    <th>{t.teachingTracking.colTeacher}</th>
                    <th>{t.manageTeaching.colDayPeriod}</th>
                    <th style={{ width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {teachings.map((teach, idx) => (
                    <tr key={teach.is_teachId}>
                      <td className="text-secondary align-middle">{idx + 1}</td>
                      <td className="align-middle fw-medium">{teach.isubject_isid}</td>
                      <td className="align-middle arabic-text">{teach.isubject_name}</td>
                      <td className="align-middle">{teach.t_name || <span className="text-muted">-</span>}</td>
                      <td className="align-middle">{renderSlotSummary(teach.slot_summary)}</td>
                      <td className="align-middle text-center">
                        <Link
                          to={`/islamic/list-teach-tracking/${teach.is_teachId}`}
                          className="btn btn-xs btn-outline-primary"
                        >
                          <i className="ri-contacts-book-line me-1"></i>
                          {t.teachingTracking.viewLogBtn}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </React.Fragment>
  );
}
